import React, { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from "react";
import { getEmployeeUserListAgainstRole } from "../../Service/AllActivityTrackerAPI";
import {
  createTask as postCreateTaskAPI,
  updateTask as putUpdateTaskAPI,
  getAllTasksList,
  getTaskListById as getTaskListByIdAPI,
} from "../../Service/AllTaskTrackerAPI";
import {
  getAllProjects as getAllProjectsAPI,
  createProject as postCreateProjectAPI,
  updateProject as putUpdateProjectAPI,
} from "../../Service/AllProjectTrackerAPI";

// Storage keys for persistent task and project data
const STORAGE_PROJECTS_KEY = "PETROPULSE_PROJECTS_MASTER_KEY";
const STORAGE_TASKS_KEY = "PETROPULSE_TASKS_MASTER_KEY";

// Export empty array for backwards compatibility with any remaining legacy imports
export const MASTER_EMPLOYEES = [];

// Helper to format ISO date to readable string
export const formatDisplayDate = (dateStr) => {
  if (!dateStr) return "--";
  const parts = String(dateStr).split("-");
  if (parts.length === 3) {
    const d = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
    if (!isNaN(d.getTime())) {
      return d.toLocaleDateString("en-US", { day: "2-digit", month: "short", year: "numeric" });
    }
  }
  const parsed = new Date(dateStr);
  if (!isNaN(parsed.getTime())) {
    return parsed.toLocaleDateString("en-US", { day: "2-digit", month: "short", year: "numeric" });
  }
  return dateStr;
};

// Helper to check if a deadline is overdue
export const isTaskOverdue = (task) => {
  if (!task || !task.deadline) return false;
  if (task.status === "Completed") return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const deadlineParts = String(task.deadline).split("-");
  if (deadlineParts.length === 3) {
    const due = new Date(Number(deadlineParts[0]), Number(deadlineParts[1]) - 1, Number(deadlineParts[2]));
    due.setHours(23, 59, 59, 999);
    return due.getTime() < today.getTime();
  }
  return false;
};

// Helper to convert usernames like "sebestin.saldana" into Title Case "Sebestin Saldana"
export const formatToTitleCase = (str) => {
  if (!str) return "";
  return String(str)
    .replace(/@.*$/, "")
    .split(/[._\-\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
};

// Helper to normalize username for payloads like "sebestin.saldana"
export const normalizeToUsername = (str) => {
  if (!str) return "";
  return String(str).trim().toLowerCase().replace(/\s+/g, ".");
};

// Convert status input to backend enum: PENDING, IN_PROGRESS, COMPLETED, ON_HOLD, CANCELLED
export const toApiStatus = (statusStr) => {
  if (!statusStr) return "PENDING";
  const normalized = String(statusStr).trim().toUpperCase().replace(/[\s-]+/g, "_");
  if (normalized === "IN_PROGRESS" || normalized === "INPROGRESS") return "IN_PROGRESS";
  if (normalized === "COMPLETED" || normalized === "COMPLETE" || normalized === "DONE") return "COMPLETED";
  if (normalized === "ON_HOLD" || normalized === "ONHOLD" || normalized === "HOLD" || normalized === "PAUSED") return "ON_HOLD";
  if (normalized === "CANCELLED" || normalized === "CANCELED" || normalized === "CANCEL") return "CANCELLED";
  return "PENDING";
};

// Convert API status enum to UI display string
export const toLocalStatus = (statusStr) => {
  const apiStatus = toApiStatus(statusStr);
  switch (apiStatus) {
    case "IN_PROGRESS":
      return "In Progress";
    case "COMPLETED":
      return "Completed";
    case "ON_HOLD":
      return "On Hold";
    case "CANCELLED":
      return "Cancelled";
    case "PENDING":
    default:
      return "Pending";
  }
};

// Initial Projects requested: PKI, Petropulse, Mobile App
// Zero fake data: starts empty and loaded dynamically from API or user actions
export const INITIAL_PROJECTS = [];
export const INITIAL_EMPLOYEES = [];
export const INITIAL_TASKS = [];

const TaskManagementContext = createContext(null);

export const TaskManagementProvider = ({ children }) => {
  // Projects State initialized from localStorage cache (ignoring any legacy fake projects) or empty array
  const [projects, setProjects] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_PROJECTS_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          return parsed.filter(
            (p) => p && !["PRJ-PKI", "PRJ-PETROPULSE", "PRJ-MOBILE"].includes(p.id)
          );
        }
      }
    } catch (e) {}
    return [];
  });

  // Keep localStorage in sync when projects change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_PROJECTS_KEY, JSON.stringify(projects));
    } catch (e) {}
  }, [projects]);

  // Tasks State initialized from localStorage cache (ignoring any legacy fake tasks) or empty array
  const [tasks, setTasks] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_TASKS_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          return parsed.filter(
            (t) =>
              t &&
              t.id &&
              !/^TSK-10[1-9]$|^TSK-110$/.test(String(t.id)) &&
              !/^(NOTE-10[1-9])$/.test(String(t.id)) &&
              t.title !== "Real-time Desk Tracking API Integration & Live Sync" &&
              t.title !== "Public Key Infrastructure Certificate Revocation List (CRL) Sync"
          );
        }
      }
    } catch (e) {}
    return [];
  });

  // Keep localStorage in sync when tasks change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_TASKS_KEY, JSON.stringify(tasks));
    } catch (e) {}
  }, [tasks]);

  // Dynamic Employee List initialized empty until fetched from server API
  const [employees, setEmployees] = useState([]);

  const [isFetchingEmployees, setIsFetchingEmployees] = useState(false);

  // Fetch employees list from server:
  // Calls https://petropulse-test.petrotec.com/User/employeuserlistagainstrole or /User/getdestDetails?role=Employee&fkUserId=163ad82d-9f74-4745-bc7c-223ca13fc33
  const fetchEmployees = useCallback(async () => {
    setIsFetchingEmployees(true);
    try {
      const targetRole = "Employee";
      const sessionPk =
        sessionStorage.getItem("pkUserId") ||
        localStorage.getItem("pkUserId") ||
        sessionStorage.getItem("userId") ||
        localStorage.getItem("userId") ||
        "163ad82d-9f74-4745-bc7c-223ca13fc33";

      console.log("--> [TaskManagementContext] Fetching employees via getEmployeeUserListAgainstRole:", {
        role: targetRole,
        fkUserId: sessionPk,
      });

      const res = await getEmployeeUserListAgainstRole(targetRole, sessionPk);
      console.log("--> [TaskManagementContext] getEmployeeUserListAgainstRole result:", res);

      const rawRecords = Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res?.data?.data)
        ? res.data.data
        : Array.isArray(res?.data?.list)
        ? res.data.list
        : Array.isArray(res?.data?.result)
        ? res.data.result
        : Array.isArray(res?.data?.userList)
        ? res.data.userList
        : Array.isArray(res?.data?.employees)
        ? res.data.employees
        : Array.isArray(res?.data?.perList)
        ? res.data.perList
        : null;

      if (rawRecords && rawRecords.length > 0) {
        const userMap = new Map();

        rawRecords.forEach((item, idx) => {
          if (!item) return;

          let obj = item;
          // Support stringified JSON elements, e.g. "{\"user_name\": \"sebestin.saldana\"}"
          if (typeof item === "string") {
            try {
              obj = JSON.parse(item);
            } catch (e) {
              obj = { user_name: item };
            }
          }

          const rawUsername =
            obj.user_name ||
            obj.userName ||
            obj.username ||
            obj.deskusername ||
            "";

          const firstName = obj.firstName || obj.firstname || "";
          const lastName = obj.lastName || obj.lastname || "";
          const combinedName = [firstName, lastName].filter(Boolean).join(" ");

          const rawDisplayName =
            combinedName ||
            obj.fullName ||
            obj.fullname ||
            obj.empName ||
            obj.empname ||
            obj.name ||
            "";

          // Format clean human name: e.g. "sebestin.saldana" -> "Sebestin Saldana"
          const cleanName =
            (rawDisplayName && !rawDisplayName.includes(".") && /[A-Z]/.test(rawDisplayName))
              ? rawDisplayName
              : formatToTitleCase(rawDisplayName || rawUsername || `Employee ${idx + 1}`);

          // Normalized username for payload: e.g. "sebestin.saldana"
          const cleanUsername = normalizeToUsername(rawUsername || rawDisplayName || cleanName);

          const dept = obj.department || obj.dept || obj.deptName || "Software Team";
          const userRole = obj.role || obj.designation || obj.userRole || "Employee";
          const email = obj.email || (cleanUsername ? `${cleanUsername}@petrotec.com` : "");

          const key = cleanUsername || cleanName.toLowerCase();
          if (!userMap.has(key)) {
            userMap.set(key, {
              id: cleanUsername,
              name: cleanName,
              userName: cleanUsername,
              user_name: cleanUsername,
              pkUserId: obj.pkUserId || obj.id || cleanUsername,
              dept,
              role: userRole,
              email,
              raw: obj,
            });
          }
        });

        const team = Array.from(userMap.values());
        if (team.length > 0) {
          setEmployees(team);
          return team;
        }
      }
    } catch (e) {
      console.warn("--> [TaskManagementContext] Could not load dynamic employees via getEmployeeUserListAgainstRole:", e);
    } finally {
      setIsFetchingEmployees(false);
    }
    return null;
  }, []);

  // Fetch employees on mount once
  const hasFetchedEmployeesRef = useRef(false);
  useEffect(() => {
    if (!hasFetchedEmployeesRef.current) {
      hasFetchedEmployeesRef.current = true;
      fetchEmployees();
    }
  }, [fetchEmployees]);

  const [isFetchingProjects, setIsFetchingProjects] = useState(false);

  // Dynamic fetch of real projects from server: GET /projects/getAllProjectList
  const fetchProjects = useCallback(async () => {
    setIsFetchingProjects(true);
    try {
      const sessionPk =
        sessionStorage.getItem("pkUserId") ||
        localStorage.getItem("pkUserId") ||
        sessionStorage.getItem("userId") ||
        localStorage.getItem("userId") ||
        "5cb01090-dfec-4710-88c1-1a4b8cc527e2";
      const sessionRole =
        sessionStorage.getItem("role") ||
        sessionStorage.getItem("userRole") ||
        "Manager";

      console.log("--> [TaskManagementContext] Fetching projects via getAllProjectsAPI:", {
        pkUserId: sessionPk,
        role: sessionRole,
      });

      const res = await getAllProjectsAPI(sessionPk, sessionRole);
      console.log("--> [TaskManagementContext] getAllProjectsAPI response:", res);

      let rawRecords = null;
      if (res && res.data) {
        if (Array.isArray(res.data)) {
          rawRecords = res.data;
        } else if (Array.isArray(res.data.data)) {
          rawRecords = res.data.data;
        } else if (Array.isArray(res.data.list)) {
          rawRecords = res.data.list;
        } else if (Array.isArray(res.data.result)) {
          rawRecords = res.data.result;
        } else if (Array.isArray(res.data.projects)) {
          rawRecords = res.data.projects;
        } else if (Array.isArray(res.data.projectList)) {
          rawRecords = res.data.projectList;
        }
      }

      if (rawRecords && rawRecords.length > 0) {
        const mapped = rawRecords
          .map((item, idx) => {
            if (!item) return null;
            let obj = item;
            if (typeof item === "string") {
              try {
                obj = JSON.parse(item);
              } catch (e) {
                obj = { projectName: item };
              }
            }
            if (!obj || typeof obj !== "object") return null;

            const projId = obj.id || obj.projectId || obj.project_id || obj.pkProjectId || (idx + 1);
            const projName = obj.projectName || obj.project_name || obj.name || obj.title || `Project ${projId}`;
            const projCode = obj.projectCode || obj.project_code || obj.code || `PRJ-${projId}`;

            const rawStatus = (obj.status || "IN_PROGRESS").toString().toUpperCase().replace(/\s+/g, "_");
            const localStatus =
              rawStatus === "COMPLETED"
                ? "Completed"
                : rawStatus === "PENDING"
                ? "Pending"
                : "In Progress";

            return {
              id: projId,
              rawId: obj.id || projId,
              code: projCode,
              name: projName,
              projectName: projName,
              description: obj.description || obj.desc || "",
              startDate: obj.startDate || obj.start_date || new Date().toISOString().split("T")[0],
              endDate: obj.endDate || obj.end_date || "",
              status: localStatus,
              rawStatus: rawStatus,
              progress: typeof obj.progress === "number" ? obj.progress : (localStatus === "Completed" ? 100 : 50),
              department: obj.department || obj.dept || "Software Team",
              manager: obj.manager || obj.managerName || "Manager",
              priority: obj.priority || "Medium",
              color: obj.color || "#2563eb",
              raw: obj,
            };
          })
          .filter(Boolean);

        if (mapped.length > 0) {
          setProjects(mapped);
          return mapped;
        }
      }
    } catch (e) {
      console.warn("--> [TaskManagementContext] Could not load projects via getAllProjectsAPI:", e);
    } finally {
      setIsFetchingProjects(false);
    }
    return [];
  }, []);

  // Fetch projects on mount once
  const hasFetchedProjectsRef = useRef(false);
  useEffect(() => {
    if (!hasFetchedProjectsRef.current) {
      hasFetchedProjectsRef.current = true;
      fetchProjects();
    }
  }, [fetchProjects]);

  const [isFetchingTasks, setIsFetchingTasks] = useState(false);

  // Fetch all tasks for manager: GET /tasks/getAllTasksList
  const fetchTasks = useCallback(async () => {
    setIsFetchingTasks(true);
    try {
      console.log("--> [TaskManagementContext] Fetching manager tasks via getAllTasksList");
      const response = await getAllTasksList();
      console.log("--> [TaskManagementContext] getAllTasksList response:", response);

      let rawList = [];
      if (response && response.data) {
        if (Array.isArray(response.data)) {
          rawList = response.data;
        } else if (Array.isArray(response.data.data)) {
          rawList = response.data.data;
        } else if (Array.isArray(response.data.tasks)) {
          rawList = response.data.tasks;
        } else if (Array.isArray(response.data.taskList)) {
          rawList = response.data.taskList;
        } else if (Array.isArray(response.data.allTasks)) {
          rawList = response.data.allTasks;
        } else if (Array.isArray(response.data.list)) {
          rawList = response.data.list;
        } else if (Array.isArray(response.data.result)) {
          rawList = response.data.result;
        }
      }

      if (Array.isArray(rawList)) {
        const mapped = rawList
          .map((item, idx) => {
            if (!item) return null;
            let st = item;
            if (typeof item === "string") {
              try {
                st = JSON.parse(item);
              } catch (e) {
                st = { taskName: item };
              }
            }
            if (!st || typeof st !== "object") return null;

            const taskId = st.id || st.taskId || st.task_id || st.pkTaskId;
            const displayId = taskId ? "TSK-" + taskId : "TSK-" + (idx + 1);

            const title = st.taskName || st.task_name || st.title || st.name || "Untitled Task";
            const description = st.description || st.desc || "";

            const rawApiStatus = toApiStatus(st.status || "PENDING");
            const localStatus = toLocalStatus(st.status || "PENDING");

            const rawPriority = (st.priority || "HIGH").toString().toUpperCase();
            const localPriority =
              rawPriority === "LOW" ? "Low" : rawPriority === "MEDIUM" ? "Medium" : "High";

            const rawAssignee =
              st.assignedTo ||
              st.assigned_to ||
              st.assignee ||
              st.assigneeName ||
              st.userName ||
              st.user_name ||
              st.employeeName ||
              "";

            const displayAssignee =
              rawAssignee && !rawAssignee.includes(".") && /[A-Z]/.test(rawAssignee)
                ? rawAssignee
                : formatToTitleCase(rawAssignee);
            const usernameAssignee = normalizeToUsername(rawAssignee || displayAssignee);

            const rawProjId = st.projectId || st.project_id || 1;
            const directProjName = st.projectName || st.project_name || st.projectTitle || st.project;

            const matchedProject = projects.find(
              (p) =>
                String(p.id) === String(rawProjId) ||
                String(p.rawId) === String(rawProjId) ||
                (directProjName && String(p.name).toLowerCase() === String(directProjName).toLowerCase())
            );

            const projName =
              (matchedProject && (matchedProject.name || matchedProject.projectName)) ||
              (directProjName && !/^Project\s+\d+$/i.test(directProjName.trim()) ? directProjName : "") ||
              (matchedProject ? (matchedProject.name || matchedProject.projectName) : directProjName || `Project ${rawProjId}`);

            const startDate = st.startDate || st.start_date || new Date().toISOString().split("T")[0];
            const dueDate = st.dueDate || st.due_date || st.deadline || st.endDate || st.end_date || startDate;

            return {
              id: displayId,
              rawId: taskId || null,
              projectId: rawProjId,
              projectName: projName,
              title: title,
              taskName: title,
              description: description,
              assigneeId: displayAssignee,
              assigneeName: displayAssignee,
              assignedTo: usernameAssignee,
              assigneeUserName: usernameAssignee,
              createdByType: st.createdByType || st.created_by_type || "MANAGER",
              createdById: st.createdById || st.created_by_id || "",
              createdByName: st.createdByName || st.created_by || "Manager",
              startDate: startDate,
              dueDate: dueDate,
              deadline: dueDate,
              endDate: dueDate,
              priority: localPriority,
              status: localStatus,
              rawStatus: rawApiStatus,
              progress: typeof st.progress === "number" ? st.progress : (localStatus === "Completed" ? 100 : localStatus === "In Progress" ? 50 : 0),
              workNotes: Array.isArray(st.workNotes) ? st.workNotes : [],
              tags: Array.isArray(st.tags) ? st.tags : [projName || "General"],
            };
          })
          .filter(Boolean);

        setTasks(mapped);
        return mapped;
      }
    } catch (err) {
      console.warn("--> [TaskManagementContext] fetchTasks error:", err);
    } finally {
      setIsFetchingTasks(false);
    }
    return [];
  }, [projects]);

  // Fetch tasks on mount once
  const hasFetchedTasksRef = useRef(false);
  useEffect(() => {
    if (!hasFetchedTasksRef.current) {
      hasFetchedTasksRef.current = true;
      fetchTasks();
    }
  }, [fetchTasks]);

  // Compute tasks with dynamic overdue flag and dynamic project resolution
  const enrichedTasks = useMemo(() => {
    const projectMap = new Map();
    projects.forEach((p) => {
      const pName = p.name || p.projectName;
      if (!pName) return;
      if (p.id !== undefined && p.id !== null) {
        projectMap.set(String(p.id), pName);
      }
      if (p.rawId !== undefined && p.rawId !== null) {
        projectMap.set(String(p.rawId), pName);
      }
      if (p.code) {
        projectMap.set(String(p.code), pName);
      }
    });

    return tasks.map((task) => {
      const overdue = isTaskOverdue(task);

      // Determine proper project name:
      let resolvedProjectName = "";

      // 1. If task already has a real name that isn't generic "Project <id>"
      if (task.projectName && !/^Project\s+\d+$/i.test(task.projectName.trim())) {
        resolvedProjectName = task.projectName;
      }

      // 2. Lookup in projectMap by task.projectId
      if (!resolvedProjectName && task.projectId !== undefined && task.projectId !== null) {
        const found = projectMap.get(String(task.projectId));
        if (found) {
          resolvedProjectName = found;
        }
      }

      // 3. Fallback to matched project or General Project
      if (!resolvedProjectName) {
        const matched = projects.find(
          (p) =>
            String(p.id) === String(task.projectId) ||
            String(p.rawId) === String(task.projectId) ||
            (task.projectName && String(p.name).toLowerCase() === String(task.projectName).toLowerCase())
        );
        if (matched) {
          resolvedProjectName = matched.name || matched.projectName;
        }
      }

      if (!resolvedProjectName) {
        resolvedProjectName =
          task.projectName ||
          (projects.length > 0 ? (projects[0].name || projects[0].projectName) : "General Project");
      }

      return {
        ...task,
        projectName: resolvedProjectName,
        isOverdue: overdue,
        displayStatus: overdue && task.status !== "Completed" ? "Overdue" : task.status,
      };
    });
  }, [tasks, projects]);

  // Create Task (Used by Manager Task Assignment & Employee Create My Task)
  const createTask = useCallback(async (taskData) => {
    const sessionUserId = sessionStorage.getItem("pkUserId") || sessionStorage.getItem("userId") || "";
    const sessionUserName = [sessionStorage.getItem("firstName"), sessionStorage.getItem("lastName")].filter(Boolean).join(" ") || sessionStorage.getItem("userName") || "Manager";
    const sessionDept = sessionStorage.getItem("department") || "Operations";

    const matchedProject = projects.find(
      (p) =>
        String(p.id) === String(taskData.projectId) ||
        String(p.rawId) === String(taskData.projectId) ||
        String(p.code) === String(taskData.projectId) ||
        (taskData.projectName && String(p.name).toLowerCase() === String(taskData.projectName).toLowerCase()) ||
        (taskData.projectName && String(p.projectName).toLowerCase() === String(taskData.projectName).toLowerCase())
    ) || {
      id: taskData.projectId || (projects[0] ? projects[0].id : 1),
      name: taskData.projectName || (projects[0] ? (projects[0].name || projects[0].projectName) : "General Project"),
    };

    let parsedProjectId = matchedProject.id;
    if (typeof parsedProjectId === "string") {
      const num = parseInt(parsedProjectId.replace(/\D/g, ""), 10);
      parsedProjectId = isNaN(num) ? 1 : num;
    }

    const resolvedProjectName =
      matchedProject.name ||
      matchedProject.projectName ||
      taskData.projectName ||
      `Project ${parsedProjectId}`;

    const matchedEmployee = (employees || []).find(
      (emp) =>
        String(emp.user_name) === String(taskData.assignedTo) ||
        String(emp.userName) === String(taskData.assignedTo) ||
        String(emp.user_name) === String(taskData.assigneeUserName) ||
        String(emp.userName) === String(taskData.assigneeUserName) ||
        String(emp.id) === String(taskData.assigneeId) ||
        String(emp.name) === String(taskData.assigneeId) ||
        String(emp.name) === String(taskData.assigneeName)
    );

    // Payload username to send to backend (e.g. "sebestin.saldana")
    const assignedPayloadUsername =
      taskData.assigneeUserName ||
      taskData.assignedTo ||
      matchedEmployee?.user_name ||
      matchedEmployee?.userName ||
      (matchedEmployee?.name ? normalizeToUsername(matchedEmployee.name) : "") ||
      (taskData.assigneeName ? normalizeToUsername(taskData.assigneeName) : "") ||
      "sebestin.saldana";

    // Clean display name for local UI / cards (e.g. "Sebestin Saldana")
    const displayEmpName =
      matchedEmployee?.name ||
      taskData.assigneeName ||
      taskData.assigneeId ||
      formatToTitleCase(assignedPayloadUsername);

    const creatorName = taskData.createdByName || sessionUserName;

    // Status formatting - initial task creation is strictly PENDING
    const apiStatus = toApiStatus(taskData.status || "PENDING");
    const localStatus = toLocalStatus(apiStatus);

    // Priority formatting
    const rawPriority = (taskData.priority || "HIGH").toString().toUpperCase();
    const apiPriority =
      rawPriority === "URGENT" || rawPriority === "HIGH"
        ? "HIGH"
        : rawPriority === "LOW"
        ? "LOW"
        : "MEDIUM";

    const startDate = taskData.startDate || new Date().toISOString().split("T")[0];
    const dueDate = taskData.deadline || taskData.endDate || taskData.dueDate || startDate;
    const taskTitle = (taskData.taskName || taskData.title || "").trim();
    const taskDescription = (taskData.description || "").trim();

    // Payload for backend: send username like "sebestin.saldana" and resolved project name
    const payload = {
      taskName: taskTitle,
      description: taskDescription,
      projectId: parsedProjectId,
      projectName: resolvedProjectName,
      project_name: resolvedProjectName,
      assignedTo: assignedPayloadUsername, // sends "sebestin.saldana"
      status: apiStatus,
      priority: apiPriority,
      startDate: startDate,
      dueDate: dueDate,
    };

    console.log("--> [TaskManagementContext] Calling postCreateTaskAPI with payload:", payload);

    let serverTask = null;
    let apiMessage = "Task created successfully.";

    try {
      const response = await postCreateTaskAPI(payload);
      console.log("--> [TaskManagementContext] postCreateTaskAPI response:", response);
      if (response && response.data) {
        if (response.data.data) {
          serverTask = response.data.data;
        } else if (response.data.id || response.data.taskName) {
          serverTask = response.data;
        }
        if (response.data.message) {
          apiMessage = response.data.message;
        }
      }
    } catch (apiErr) {
      console.warn("--> [TaskManagementContext] Server createTask API failed, saving to local task state:", apiErr);
      if (apiErr?.response?.data?.message) {
        apiMessage = apiErr.response.data.message;
      }
    }

    const serverId = serverTask?.id ? String(serverTask.id) : null;
    const newId = serverId ? "TSK-" + serverId : "TSK-" + Date.now().toString().slice(-4);

    const newTask = {
      id: newId,
      rawId: serverTask?.id || null,
      projectId: parsedProjectId,
      projectName: resolvedProjectName,
      title: serverTask?.taskName || taskTitle,
      taskName: serverTask?.taskName || taskTitle,
      description: serverTask?.description !== undefined ? serverTask.description : taskDescription,
      assigneeId: displayEmpName,
      assigneeName: displayEmpName,
      assignedTo: assignedPayloadUsername,
      assigneeUserName: assignedPayloadUsername,
      assigneeDept: matchedEmployee ? matchedEmployee.dept : sessionDept,
      assigneeRole: matchedEmployee ? matchedEmployee.role : "Employee",
      createdByType: taskData.createdByType || "MANAGER",
      createdById: taskData.createdById || sessionUserId,
      createdByName: creatorName,
      createdAt: serverTask?.createdAt || new Date().toISOString(),
      updatedAt: serverTask?.updatedAt || new Date().toISOString(),
      startDate: serverTask?.startDate || startDate,
      deadline: serverTask?.dueDate || dueDate,
      endDate: serverTask?.dueDate || dueDate,
      dueDate: serverTask?.dueDate || dueDate,
      status: localStatus,
      rawStatus: serverTask?.status || apiStatus,
      priority: taskData.priority === "Urgent" ? "High" : (taskData.priority || "Medium"),
      rawPriority: serverTask?.priority || apiPriority,
      progress: 0,
      tags: taskData.tags || ["General"],
      workNotes: taskData.initialNote
        ? [
          {
            id: `NOTE-${Date.now()}`,
            author: creatorName,
            authorRole: taskData.createdByType === "MANAGER" ? "Project Manager" : "Employee",
            timestamp: new Date().toISOString(),
            text: taskData.initialNote.trim(),
            progressSnapshot: 0,
            statusSnapshot: "Pending",
          },
        ]
        : [],
    };

    setTasks((prev) => [newTask, ...prev]);
    // Refresh server tasks list in background
    setTimeout(() => {
      fetchTasks();
    }, 500);

    return {
      ...newTask,
      message: apiMessage,
    };
  }, [projects, employees, fetchTasks]);

  // Update Task Status & Work Notes (Used by My Tasks & Manager Tracking)
  // Calls PUT /tasks/updateTask/{id}
  const updateTask = useCallback(async (taskId, updates = {}) => {
    const targetTask = tasks.find(
      (t) =>
        t.id === taskId ||
        String(t.rawId) === String(taskId) ||
        (typeof taskId === "string" && typeof t.id === "string" && t.id.replace(/\D/g, "") === taskId.replace(/\D/g, ""))
    );

    const backendId =
      targetTask?.rawId ||
      (typeof taskId === "string" ? taskId.replace(/\D/g, "") || taskId : taskId) ||
      taskId;

    // Status formatting: PENDING, IN_PROGRESS, COMPLETED, ON_HOLD, CANCELLED
    const apiStatus = toApiStatus(updates.status || targetTask?.status || "PENDING");
    const localStatus = toLocalStatus(apiStatus);

    // Priority formatting
    const rawPriority = (updates.priority || targetTask?.priority || "HIGH").toString().toUpperCase();
    const apiPriority =
      rawPriority === "URGENT" || rawPriority === "HIGH"
        ? "HIGH"
        : rawPriority === "LOW"
        ? "LOW"
        : "MEDIUM";

    let parsedProjectId = updates.projectId || targetTask?.projectId || 1;
    if (typeof parsedProjectId === "string") {
      const num = parseInt(parsedProjectId.replace(/\D/g, ""), 10);
      parsedProjectId = isNaN(num) ? 1 : num;
    }

    const matchedProject = projects.find(
      (p) =>
        String(p.id) === String(parsedProjectId) ||
        String(p.rawId) === String(parsedProjectId) ||
        (updates.projectName && String(p.name).toLowerCase() === String(updates.projectName).toLowerCase()) ||
        (targetTask?.projectName && String(p.name).toLowerCase() === String(targetTask.projectName).toLowerCase())
    );

    const resolvedProjectName =
      updates.projectName ||
      matchedProject?.name ||
      matchedProject?.projectName ||
      targetTask?.projectName ||
      `Project ${parsedProjectId}`;

    const assignedPayloadUsername =
      updates.assigneeUserName ||
      updates.assignedTo ||
      targetTask?.assigneeUserName ||
      targetTask?.assignedTo ||
      normalizeToUsername(updates.assigneeName || targetTask?.assigneeName || "sebestin.saldana") ||
      "sebestin.saldana";

    const startDate = updates.startDate || targetTask?.startDate || new Date().toISOString().split("T")[0];
    const dueDate = updates.dueDate || updates.deadline || updates.endDate || targetTask?.dueDate || startDate;

    const payload = {
      taskName: (updates.taskName || updates.title || targetTask?.title || "Untitled Task").trim(),
      description: updates.description !== undefined ? updates.description : (targetTask?.description || ""),
      projectId: parsedProjectId,
      projectName: resolvedProjectName,
      project_name: resolvedProjectName,
      assignedTo: assignedPayloadUsername,
      status: apiStatus,
      priority: apiPriority,
      startDate: startDate,
      dueDate: dueDate,
    };

    console.log("--> [TaskManagementContext] Calling putUpdateTaskAPI ID:", backendId, "with payload:", payload);

    let apiResponse = null;
    let apiMessage = "Task updated successfully.";

    if (backendId && typeof putUpdateTaskAPI === "function") {
      try {
        const res = await putUpdateTaskAPI(backendId, payload);
        console.log("--> [TaskManagementContext] putUpdateTaskAPI response:", res);
        apiResponse = res;
        if (res?.data?.message) {
          apiMessage = res.data.message;
        }
      } catch (err) {
        console.warn("--> [TaskManagementContext] putUpdateTaskAPI error:", err);
        if (err?.response?.data?.message) {
          apiMessage = err.response.data.message;
        }
        throw err;
      }
    }

    setTasks((prev) =>
      prev.map((t) => {
        if (
          t.id === taskId ||
          String(t.rawId) === String(taskId) ||
          (typeof taskId === "string" && typeof t.id === "string" && t.id.replace(/\D/g, "") === taskId.replace(/\D/g, ""))
        ) {
          const updated = {
            ...t,
            ...updates,
            title: updates.taskName || updates.title || t.title,
            taskName: updates.taskName || updates.title || t.taskName,
            description: updates.description !== undefined ? updates.description : t.description,
            projectId: parsedProjectId,
            projectName: resolvedProjectName,
            assignedTo: assignedPayloadUsername,
            assigneeUserName: assignedPayloadUsername,
            status: localStatus,
            rawStatus: apiStatus,
            priority:
              apiPriority === "HIGH"
                ? "High"
                : apiPriority === "LOW"
                ? "Low"
                : "Medium",
            startDate,
            dueDate,
            deadline: dueDate,
            endDate: dueDate,
          };
          if (localStatus === "Completed" && updates.progress === undefined) {
            updated.progress = 100;
          } else if (localStatus === "Pending" && updates.progress === undefined && t.progress === 100) {
            updated.progress = 0;
          }
          return updated;
        }
        return t;
      })
    );

    // Refresh tasks in background
    setTimeout(() => {
      fetchTasks();
    }, 500);

    return {
      success: true,
      message: apiMessage,
      data: apiResponse?.data,
    };
  }, [tasks, projects, fetchTasks]);

  // Dynamic fetch of real tasks for an employee from server
  const fetchEmployeeTasks = useCallback(async (pkUserId) => {
    try {
      const res = await getTaskListByIdAPI(pkUserId);
      const rawList = Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res?.data?.data)
        ? res.data.data
        : Array.isArray(res?.data?.list)
        ? res.data.list
        : [];

      if (rawList && rawList.length > 0) {
        const mapped = rawList
          .map((item) => {
            if (!item) return null;
            let st = item;
            if (typeof item === "string") {
              try {
                st = JSON.parse(item);
              } catch (e) {
                st = { taskName: item };
              }
            }
            if (!st || typeof st !== "object") return null;

            const rawApiStatus = toApiStatus(st.status || "PENDING");
            const localStatus = toLocalStatus(st.status || "PENDING");

            const rawPriority = (st.priority || "HIGH").toString().toUpperCase();
            const localPriority =
              rawPriority === "LOW" ? "Low" : rawPriority === "MEDIUM" ? "Medium" : "High";

            const rawAssignee = st.assignedTo || st.assignee || st.assigneeName || st.assigneeId || "";
            const displayAssignee =
              rawAssignee && !rawAssignee.includes(".") && /[A-Z]/.test(rawAssignee)
                ? rawAssignee
                : formatToTitleCase(rawAssignee);
            const usernameAssignee = normalizeToUsername(rawAssignee || displayAssignee);

            const rawProjId = st.projectId || st.project_id || 1;
            const directProjName = st.projectName || st.project_name || st.projectTitle || st.project;

            const matchedProject = projects.find(
              (p) =>
                String(p.id) === String(rawProjId) ||
                String(p.rawId) === String(rawProjId) ||
                (directProjName && String(p.name).toLowerCase() === String(directProjName).toLowerCase())
            );

            const projName =
              (matchedProject && (matchedProject.name || matchedProject.projectName)) ||
              (directProjName && !/^Project\s+\d+$/i.test(directProjName.trim()) ? directProjName : "") ||
              (matchedProject ? (matchedProject.name || matchedProject.projectName) : directProjName || `Project ${rawProjId}`);

            return {
              id: st.id ? "TSK-" + st.id : "TSK-" + Date.now(),
              rawId: st.id || null,
              projectId: rawProjId,
              projectName: projName,
              title: st.taskName || st.title || "Untitled Task",
              taskName: st.taskName || st.title || "Untitled Task",
              description: st.description || "",
              assigneeId: displayAssignee,
              assigneeName: displayAssignee,
              assignedTo: usernameAssignee,
              assigneeUserName: usernameAssignee,
              createdByType: st.createdByType || "MANAGER",
              createdById: st.createdById || "",
              createdByName: st.createdByName || "Manager",
              startDate: st.startDate || new Date().toISOString().split("T")[0],
              dueDate: st.dueDate || st.deadline || st.endDate || new Date().toISOString().split("T")[0],
              deadline: st.dueDate || st.deadline || st.endDate || new Date().toISOString().split("T")[0],
              priority: localPriority,
              status: localStatus,
              rawStatus: rawApiStatus,
              progress: localStatus === "Completed" ? 100 : localStatus === "In Progress" ? 50 : 0,
              workNotes: Array.isArray(st.workNotes) ? st.workNotes : [],
              isEmployeeTask: true,
            };
          })
          .filter(Boolean);

        setTasks((prev) => {
          const existingIds = new Set(prev.map((t) => String(t.rawId || t.id)));
          const newItems = mapped.filter((m) => !existingIds.has(String(m.rawId || m.id)));
          return [...newItems, ...prev];
        });
      }
    } catch (e) {
      console.warn("--> [TaskManagementContext] fetchEmployeeTasks error:", e);
    }
  }, [projects]);

  // Add Work Note to a Task
  const addWorkNote = useCallback((taskId, { text, author, authorRole, progress, status }) => {
    if (!text || !text.trim()) return;

    setTasks((prev) =>
      prev.map((t) => {
        if (t.id === taskId) {
          const newNote = {
            id: `NOTE-${Date.now()}`,
            author: author || "Employee",
            authorRole: authorRole || "Staff",
            timestamp: new Date().toISOString(),
            text: text.trim(),
            progressSnapshot: progress !== undefined ? progress : t.progress,
            statusSnapshot: status || t.status,
          };

          const updatedNotes = [...(t.workNotes || []), newNote];
          const updated = {
            ...t,
            workNotes: updatedNotes,
          };
          if (progress !== undefined) updated.progress = progress;
          if (status) updated.status = status;
          if (status === "Completed") updated.progress = 100;
          return updated;
        }
        return t;
      })
    );
  }, []);

  // Delete Task
  const deleteTask = useCallback((taskId) => {
    setTasks((prev) => prev.filter((t) => t.id !== taskId));
  }, []);

  // Create Project API Call
  const createProject = useCallback(async (projectData) => {
    const sessionPk = sessionStorage.getItem("pkUserId") || "5cb01090-dfec-4710-88c1-1a4b8cc527e2";
    const sessionRole = sessionStorage.getItem("role") || "Manager";
    const sessionUserName = [sessionStorage.getItem("firstName"), sessionStorage.getItem("lastName")].filter(Boolean).join(" ") || sessionStorage.getItem("userName") || "Project Manager";
    const sessionDept = sessionStorage.getItem("department") || "Operations";

    const payload = {
      projectName: (projectData.name || projectData.projectName || "").trim(),
      projectCode: projectData.code ? projectData.code.toUpperCase().trim() : undefined,
      description: (projectData.description || "").trim(),
      startDate: projectData.startDate || new Date().toISOString().split("T")[0],
      endDate: projectData.endDate || "",
      department: projectData.department || sessionDept,
      manager: projectData.manager || sessionUserName,
      status: projectData.status || "IN_PROGRESS",
      priority: projectData.priority || "Medium",
    };

    let serverProject = null;
    let apiMessage = "Project created successfully.";

    try {
      if (typeof postCreateProjectAPI === "function") {
        const res = await postCreateProjectAPI(payload, sessionPk, sessionRole);
        console.log("--> [TaskManagementContext] postCreateProjectAPI res:", res);
        if (res && res.data) {
          serverProject = res.data.data || res.data;
          if (res.data.message) apiMessage = res.data.message;
        }
      }
    } catch (e) {
      console.warn("--> [TaskManagementContext] Server createProject failed:", e);
      if (e?.response?.data?.message) apiMessage = e.response.data.message;
    }

    const newId = serverProject?.id || `PRJ-${Math.floor(100 + Math.random() * 900)}`;
    const newProject = {
      id: newId,
      rawId: serverProject?.id || newId,
      code: projectData.code ? projectData.code.toUpperCase().trim() : `PP-PRJ-${newId}`,
      name: (projectData.name || projectData.projectName || "").trim(),
      projectName: (projectData.name || projectData.projectName || "").trim(),
      description: (projectData.description || "").trim(),
      startDate: projectData.startDate || new Date().toISOString().split("T")[0],
      endDate: projectData.endDate || "",
      status: projectData.status || "In Progress",
      department: projectData.department || sessionDept,
      manager: projectData.manager || sessionUserName,
      progress: Number(projectData.progress || 0),
      priority: projectData.priority || "Medium",
      color: projectData.color || "#2563eb",
    };

    setProjects((prev) => [newProject, ...prev]);
    setTimeout(() => {
      fetchProjects();
    }, 500);

    return {
      ...newProject,
      message: apiMessage,
    };
  }, [fetchProjects]);

  // Update Project API Call
  const updateProject = useCallback(async (projectId, updateData) => {
    const sessionPk = sessionStorage.getItem("pkUserId") || "5cb01090-dfec-4710-88c1-1a4b8cc527e2";
    const sessionRole = sessionStorage.getItem("role") || "Manager";

    const payload = {
      projectName: (updateData.projectName || updateData.name || "").trim(),
      description: (updateData.description || "").trim(),
      startDate: updateData.startDate,
      endDate: updateData.endDate,
      status: updateData.status,
    };

    let apiMessage = "Project updated successfully.";
    try {
      if (typeof putUpdateProjectAPI === "function") {
        const res = await putUpdateProjectAPI(projectId, payload, sessionPk, sessionRole);
        console.log("--> [TaskManagementContext] putUpdateProjectAPI res:", res);
        if (res?.data?.message) apiMessage = res.data.message;
      }
    } catch (e) {
      console.warn("--> [TaskManagementContext] putUpdateProjectAPI failed:", e);
      if (e?.response?.data?.message) apiMessage = e.response.data.message;
      throw e;
    }

    setProjects((prev) =>
      prev.map((p) => {
        if (String(p.id) === String(projectId) || String(p.rawId) === String(projectId)) {
          return {
            ...p,
            ...updateData,
            name: updateData.projectName || updateData.name || p.name,
            projectName: updateData.projectName || updateData.name || p.projectName,
          };
        }
        return p;
      })
    );

    setTimeout(() => {
      fetchProjects();
    }, 500);

    return { success: true, message: apiMessage };
  }, [fetchProjects]);

  // Manager KPI Statistics Calculation
  const managerStats = useMemo(() => {
    let total = enrichedTasks.length;
    let pending = 0;
    let inProgress = 0;
    let completed = 0;
    let onHold = 0;
    let cancelled = 0;
    let overdue = 0;
    let employeeCreated = 0;
    let managerAssigned = 0;

    enrichedTasks.forEach((t) => {
      if (t.isOverdue) overdue++;
      if (t.status === "Pending") pending++;
      else if (t.status === "Completed") completed++;
      else if (t.status === "On Hold") onHold++;
      else if (t.status === "Cancelled") cancelled++;
      else inProgress++;

      if (t.createdByType === "EMPLOYEE") employeeCreated++;
      else managerAssigned++;
    });

    return {
      total,
      pending,
      inProgress,
      completed,
      onHold,
      cancelled,
      overdue,
      employeeCreated,
      managerAssigned,
    };
  }, [enrichedTasks]);

  // Employee Personal Statistics Calculation
  const getEmployeeStats = useCallback((employeeId) => {
    const cur = String(employeeId || "").toLowerCase().trim();
    const personalTasks = enrichedTasks.filter((t) => {
      const aId = String(t.assigneeId || "").toLowerCase().trim();
      const aName = String(t.assigneeName || "").toLowerCase().trim();
      const aAssigned = String(t.assignedTo || "").toLowerCase().trim();
      const aUser = String(t.assigneeUserName || "").toLowerCase().trim();
      return (
        aId === cur ||
        aName === cur ||
        aAssigned === cur ||
        aUser === cur ||
        (cur && (aId.includes(cur) || cur.includes(aId) || aName.includes(cur) || cur.includes(aName) || aAssigned.includes(cur) || cur.includes(aAssigned)))
      );
    });
    let total = personalTasks.length;
    let pending = 0;
    let inProgress = 0;
    let completed = 0;
    let onHold = 0;
    let cancelled = 0;
    let overdue = 0;
    let myCreated = 0;

    personalTasks.forEach((t) => {
      if (t.isOverdue) overdue++;
      if (t.status === "Pending") pending++;
      else if (t.status === "Completed") completed++;
      else if (t.status === "On Hold") onHold++;
      else if (t.status === "Cancelled") cancelled++;
      else inProgress++;

      if (t.createdByType === "EMPLOYEE") myCreated++;
    });

    return {
      total,
      pending,
      inProgress,
      completed,
      onHold,
      cancelled,
      overdue,
      myCreated,
      managerAssigned: total - myCreated,
    };
  }, [enrichedTasks]);

  const value = useMemo(
    () => ({
      projects,
      isFetchingProjects,
      fetchProjects,
      tasks: enrichedTasks,
      rawTasks: tasks,
      employees,
      isFetchingEmployees,
      fetchEmployees,
      fetchEmployeeTasks,
      fetchTasks,
      isFetchingTasks,
      managerStats,
      getEmployeeStats,
      createTask,
      updateTask,
      updateTaskStatus: updateTask,
      addWorkNote,
      deleteTask,
      createProject,
      updateProject,
      formatToTitleCase,
      normalizeToUsername,
      toApiStatus,
      toLocalStatus,
    }),
    [
      projects,
      isFetchingProjects,
      fetchProjects,
      enrichedTasks,
      tasks,
      employees,
      isFetchingEmployees,
      fetchEmployees,
      fetchEmployeeTasks,
      fetchTasks,
      isFetchingTasks,
      managerStats,
      getEmployeeStats,
      createTask,
      updateTask,
      addWorkNote,
      deleteTask,
      createProject,
      updateProject,
    ]
  );

  return (
    <TaskManagementContext.Provider value={value}>
      {children}
    </TaskManagementContext.Provider>
  );
};

export const useTaskManager = () => {
  const context = useContext(TaskManagementContext);
  if (!context) {
    throw new Error("useTaskManager must be used within a TaskManagementProvider");
  }
  return context;
};

export default TaskManagementContext;
